/**
 * Payment-object presets and enums for the Odoo invoice queue.message contract.
 *
 * Shared by the Drive Away flow and the Odoo Payment Update page.
 * Renamed from `drive-away-constants.ts` when the Odoo Payment Update page was
 * introduced (see docs/superpowers/specs/2026-06-09-odoo-payment-update-design.md).
 */

export interface OdooInvoicePayment {
  ryd_payment_type: string;
  bank_name: string | null;
  masked_iban: string;
  date: string;
  amount: number;
  currency: string;
  status: string;
  payment_reference: string;
  payment_provider: string;
  note: string;
}

/** Legacy alias kept for the Drive Away flow's existing imports. */
export type DriveAwayPaymentObject = OdooInvoicePayment;

export const ODOO_PAYMENT_DEFAULTS: Record<string, OdooInvoicePayment> = {
  SEPA_BANK_TRANSFER: {
    ryd_payment_type: 'POSTPAY_BY_PARTNER',
    bank_name: 'Deutsche Bank',
    masked_iban: 'DE11 7207 0001 0032 6058 02',
    date: '',
    amount: 0,
    currency: 'EUR',
    status: 'pending',
    payment_reference: '',
    payment_provider: 'THINXNET',
    note: '',
  },
  PAYPAL: {
    ryd_payment_type: 'POSTPAY_BY_PARTNER',
    bank_name: null,
    masked_iban: '',
    date: '',
    amount: 0,
    currency: 'EUR',
    status: 'pending',
    payment_reference: '',
    payment_provider: 'PAYPAL',
    note: '',
  },
  CREDIT_CARD: {
    ryd_payment_type: 'POSTPAY_BY_PARTNER',
    bank_name: null,
    masked_iban: '',
    date: '',
    amount: 0,
    currency: 'EUR',
    status: 'pending',
    payment_reference: '',
    payment_provider: 'BRAINTREE',
    note: '',
  },
};

/** Legacy alias preserved so DriveAwayFlow's existing import keeps working. */
export const DRIVE_AWAY_PAYMENT_DEFAULTS = ODOO_PAYMENT_DEFAULTS;

export const ODOO_PAYMENT_LABELS: Record<string, string> = {
  SEPA_BANK_TRANSFER: 'SEPA Bank Transfer',
  PAYPAL: 'PayPal',
  CREDIT_CARD: 'Credit Card',
};

export const DRIVE_AWAY_PAYMENT_LABELS = ODOO_PAYMENT_LABELS;

export const REASON_CATEGORIES = [
  'Fraud',
  'Gift card',
  'Wrong pump',
  'Goodwill',
  'Test',
  'Other',
] as const;

/**
 * Known Payment Providers for Odoo queue.messages.
 * Users can select an option from this Enum helper or type a custom string.
 */
export const PAYMENT_PROVIDERS = [
  'CONCARDIS',
  'THINXNET',
  'WALLEE',
  'BRAINTREE',
  'MPGS',
  'SIBS',
  'AMAZON',
  'BP',
  'BLUECODE',
] as const;

export type PaymentProvider = (typeof PAYMENT_PROVIDERS)[number];

/**
 * Common credit card brands for CREDIT_CARD payments.
 */
export const CREDIT_CARD_BRANDS = [
  'VISA',
  'MASTERCARD',
  'AMEX',
  'DISCOVER',
  'DINERS',
  'JCB',
] as const;

export type CreditCardBrand = (typeof CREDIT_CARD_BRANDS)[number];

/**
 * Payment types supported for Odoo queue.messages.
 */
export const RYD_PAYMENT_TYPES = [
  'BANK_TRANSFER',
  'CREDIT_CARD',
  'APPLE_PAY',
  'GOOGLE_PAY',
  'AMAZON_PAY',
  'PAYCONIQ',
  'POSTPAY_BY_PARTNER',
] as const;

export type RydPaymentType = (typeof RYD_PAYMENT_TYPES)[number];

/**
 * Allowed payment statuses for an Odoo queue.message:
 * - `paid`     — payment was successfully processed.
 * - `not_paid` — payment not yet received (e.g. BANK_TRANSFER awaiting customer).
 * - `pending`  — payment is in progress / awaiting confirmation.
 */
export const PAYMENT_STATUSES = ['paid', 'not_paid', 'pending'] as const;

export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const PAYMENT_STATUS_DESCRIPTIONS: Record<PaymentStatus, string> = {
  paid: 'Payment received',
  not_paid: 'Awaiting customer',
  pending: 'In progress',
};

/**
 * Default bank-transfer details for the Odoo Payment Update page.
 * Operator can apply these in one click then edit anything.
 */
export const BANK_TRANSFER_DEFAULTS = {
  ryd_payment_type: 'BANK_TRANSFER' as const,
  bank_name: 'Deutsche Bank',
  masked_iban: 'DE11 7207 0001 0032 6058 02',
  payment_provider: 'THINXNET',
  currency: 'EUR',
};
